import React, { useEffect, useState } from 'react';
import { Image, Spin, Tooltip, Typography } from 'antd';
import { PictureOutlined, PlayCircleOutlined } from '@ant-design/icons';
import { ClassPill, PersonAvatar, Pill } from 'pages/support-shared/components';
import { colors } from 'theme/tokens';
import {
  HIDDEN_REASON_LABEL,
  LOG_LABEL_TEXT,
  NEEDS_REVIEW_STYLE,
  POST_STATUS_STYLE,
  STAGE_LABEL,
  VERDICT_STYLE,
} from './labels';
import { FeedAuthorView, FeedLogRow, FeedThumb, TestTextMatch } from './types';
import {
  classText,
  fetchMediaObjectUrl,
  fmtScore,
  isAdminMediaUrl,
  parseLoose,
  toStringList,
} from './utils';

export { Pill, PersonAvatar };

// ------------------------------------------------------------------ viên trạng thái

export const StatusPill = ({ status, deletedAt }: { status: string; deletedAt?: string | null }) => {
  const s = deletedAt ? POST_STATUS_STYLE.DELETED : POST_STATUS_STYLE[status];
  if (!s) return <Pill color={colors.textSecondary} bg={colors.surfaceMuted}>{status}</Pill>;
  return (
    <Pill color={s.color} bg={s.bg}>
      {s.label}
    </Pill>
  );
};

export const NeedsReviewPill = () => (
  <Pill color={NEEDS_REVIEW_STYLE.color} bg={NEEDS_REVIEW_STYLE.bg}>
    {NEEDS_REVIEW_STYLE.label}
  </Pill>
);

export const VerdictPill = ({ verdict, prefix }: { verdict?: string | null; prefix?: string }) => {
  if (!verdict) return null;
  const s = VERDICT_STYLE[verdict];
  const label = s?.label || verdict;
  return (
    <Pill color={s?.color || colors.textSecondary} bg={s?.bg || colors.surfaceMuted}>
      {prefix ? `${prefix}: ${label}` : label}
    </Pill>
  );
};

export const FeedClassPill = ({
  userClass,
  classLabel,
}: {
  userClass?: string | null;
  classLabel?: string | null;
}) => {
  if (!userClass && !classLabel) return null;
  return <ClassPill value={userClass || 'PUBLIC'} label={classText(userClass, classLabel)} />;
};

export const StagePill = ({ stage }: { stage: string }) => {
  const palette =
    stage === 'HUMAN'
      ? { color: colors.accentDeep, bg: colors.accentSoft }
      : stage === 'USER'
      ? { color: colors.purpleInk, bg: colors.purpleSoft }
      : stage === 'SYSTEM'
      ? { color: colors.textSecondary, bg: colors.surfaceMuted }
      : { color: colors.infoInk, bg: colors.infoSoft };
  return (
    <Pill color={palette.color} bg={palette.bg}>
      {STAGE_LABEL[stage] || stage}
    </Pill>
  );
};

/** Nhãn tab kèm viên số (không dùng Badge của antd — số cuộn bị kẹt). 0/undefined thì không vẽ. */
export const TabLabel = ({ text, count, danger }: { text: string; count?: number | null; danger?: boolean }) => (
  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
    {text}
    {count ? (
      <Pill
        color={danger ? colors.dangerInk : colors.accentDeep}
        bg={danger ? colors.dangerSoft : colors.accentSoft}
        style={{ padding: '0 7px', lineHeight: '18px' }}
      >
        {count > 99 ? '99+' : count}
      </Pill>
    ) : null}
  </span>
);

/** Dòng 2 của cột Trạng thái: lý do ẩn, hoặc cờ "Cần xem" + nguồn cờ. */
export const StatusNote = ({
  hiddenReason,
  needsReview,
  keywordVerdict,
  textVerdict,
  mediaVerdicts,
}: {
  hiddenReason?: string | null;
  needsReview?: boolean;
  keywordVerdict?: string | null;
  textVerdict?: string | null;
  mediaVerdicts?: string[] | null;
}) => {
  const parts: string[] = [];
  if (hiddenReason) parts.push(HIDDEN_REASON_LABEL[hiddenReason] || hiddenReason);
  if (needsReview) {
    const why: string[] = [];
    if (keywordVerdict === 'REVIEW') why.push('Có từ cần xem');
    if (textVerdict === 'REVIEW') why.push('AI chữ: cần xem');
    if ((mediaVerdicts || []).includes('REVIEW')) why.push('AI ảnh: cần xem');
    parts.push(why.length ? `Cần xem · ${why.join(', ')}` : 'Cần xem');
  }
  if (!parts.length) return null;
  return <div style={{ fontSize: 12, color: colors.textSecondary, marginTop: 4 }}>{parts.join(' · ')}</div>;
};

// ------------------------------------------------------------------ người

export const AuthorCell = ({
  author,
  actedBy,
  size = 32,
  onClickName,
}: {
  author?: FeedAuthorView | null;
  actedBy?: { userId: number; name: string } | null;
  size?: number;
  onClickName?: () => void;
}) => {
  if (!author) return <span style={{ color: colors.textMuted }}>—</span>;
  const isPage = author.type === 'PAGE';
  const name = author.name || (isPage ? 'HP Media' : `#${author.userId}`);
  const nameNode = onClickName ? (
    <Typography.Link onClick={onClickName} ellipsis style={{ maxWidth: '100%', fontWeight: 600 }}>
      {name}
    </Typography.Link>
  ) : (
    <Typography.Text ellipsis style={{ maxWidth: '100%', fontWeight: 600 }}>
      {name}
    </Typography.Text>
  );
  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'center', minWidth: 0 }}>
      <PersonAvatar name={name} avatar={author.avatar} size={size} />
      <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        {nameNode}
        <div style={{ fontSize: 12, color: colors.textSecondary, lineHeight: '20px' }}>
          {isPage ? (
            actedBy?.name ? (
              `do ${actedBy.name} đăng`
            ) : (
              'Trang HP Media'
            )
          ) : (
            <FeedClassPill userClass={author.userClass} classLabel={author.classLabel} />
          )}
        </div>
      </div>
    </div>
  );
};

// ------------------------------------------------------------------ ảnh / video

/** src dùng được cho <img>/<video>: file private (D39) thì tải bằng axios thành blob URL. */
export function useMediaSrc(url?: string | null) {
  const needsBlob = isAdminMediaUrl(url);
  const [state, setState] = useState<{ src?: string; loading: boolean; failed: boolean }>({
    src: needsBlob ? undefined : url || undefined,
    loading: needsBlob,
    failed: false,
  });
  useEffect(() => {
    if (!url || !isAdminMediaUrl(url)) {
      setState({ src: url || undefined, loading: false, failed: false });
      return undefined;
    }
    let alive = true;
    let objectUrl = '';
    setState({ src: undefined, loading: true, failed: false });
    fetchMediaObjectUrl(url)
      .then((value) => {
        objectUrl = value;
        if (alive) setState({ src: value, loading: false, failed: false });
        else URL.revokeObjectURL(value);
      })
      .catch(() => alive && setState({ src: undefined, loading: false, failed: true }));
    return () => {
      alive = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [url]);
  return state;
}

const boxStyle = (size: number, bg?: string | null): React.CSSProperties => ({
  width: size,
  height: size,
  flex: 'none',
  borderRadius: 8,
  overflow: 'hidden',
  background: bg || colors.surfaceMuted,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: colors.textMuted,
});

/** Ảnh nhỏ vuông (bảng, thẻ). */
export const MediaThumb = ({
  url,
  kind,
  size = 40,
  placeholderColor,
}: {
  url?: string | null;
  kind?: 'IMAGE' | 'VIDEO';
  size?: number;
  placeholderColor?: string | null;
}) => {
  const { src, loading } = useMediaSrc(url);
  return (
    <div style={{ ...boxStyle(size, placeholderColor), position: 'relative' }}>
      {loading ? (
        <Spin size="small" />
      ) : src ? (
        <img src={src} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
      ) : kind === 'VIDEO' ? (
        <PlayCircleOutlined />
      ) : (
        <PictureOutlined />
      )}
      {kind === 'VIDEO' && src && (
        <PlayCircleOutlined
          style={{ position: 'absolute', color: '#fff', fontSize: Math.round(size / 2.4), textShadow: '0 1px 4px rgba(0,0,0,.5)' }}
        />
      )}
    </div>
  );
};

/** Tối đa 3 ảnh nhỏ bên trái nội dung (01 §6.3 `thumbs`). */
export const MediaStrip = ({ thumbs, size = 40 }: { thumbs?: FeedThumb[] | null; size?: number }) => {
  if (!thumbs?.length) return null;
  return (
    <div style={{ display: 'flex', gap: 4, flex: 'none' }}>
      {thumbs.slice(0, 3).map((t, i) => (
        <MediaThumb key={i} url={t.previewUrl} kind={t.kind} size={size} />
      ))}
    </div>
  );
};

/** Ảnh lớn trong ngăn chi tiết (bấm để phóng to). */
export const MediaImage = ({
  url,
  previewUrl,
  placeholderColor,
  height = 180,
}: {
  url?: string | null;
  previewUrl?: string | null;
  placeholderColor?: string | null;
  height?: number;
}) => {
  const full = useMediaSrc(url);
  const thumb = useMediaSrc(previewUrl || url);
  if (thumb.loading) {
    return (
      <div style={{ ...boxStyle(0, placeholderColor), width: '100%', height }}>
        <Spin size="small" />
      </div>
    );
  }
  if (!thumb.src) {
    return (
      <div style={{ ...boxStyle(0, placeholderColor), width: '100%', height, fontSize: 12 }}>
        <PictureOutlined style={{ marginRight: 6 }} />
        {thumb.failed ? 'Không tải được ảnh' : 'Chưa có ảnh'}
      </div>
    );
  }
  return (
    <Image
      src={thumb.src}
      preview={full.src ? { src: full.src } : undefined}
      width="100%"
      height={height}
      style={{ objectFit: 'cover', borderRadius: 8, background: placeholderColor || colors.surfaceMuted }}
    />
  );
};

export const MediaVideo = ({ url, poster }: { url?: string | null; poster?: string | null }) => {
  const video = useMediaSrc(url);
  const cover = useMediaSrc(poster);
  if (video.loading) {
    return (
      <div style={{ ...boxStyle(0), width: '100%', height: 240 }}>
        <Spin />
      </div>
    );
  }
  if (!video.src) {
    return (
      <div style={{ ...boxStyle(0), width: '100%', height: 120, fontSize: 13 }}>
        {video.failed ? 'Không tải được video.' : 'Chưa có video.'}
      </div>
    );
  }
  return (
    <video
      controls
      preload="metadata"
      poster={cover.src}
      src={video.src}
      style={{ width: '100%', maxHeight: 480, borderRadius: 8, background: '#000' }}
    />
  );
};

// ------------------------------------------------------------------ thử câu

/** Tô các đoạn [start, end) (chỉ số UTF-16 trên câu gốc — 01 §6 cuối). */
export const HighlightedText = ({ text, matches }: { text: string; matches: TestTextMatch[] }) => {
  const sorted = [...matches]
    .filter((m) => m.end > m.start && m.start >= 0 && m.end <= text.length)
    .sort((a, b) => a.start - b.start);
  const parts: React.ReactNode[] = [];
  let cursor = 0;
  sorted.forEach((m, i) => {
    if (m.start < cursor) return; // chồng lấn: bỏ đoạn sau
    if (m.start > cursor) parts.push(<span key={`t${i}`}>{text.slice(cursor, m.start)}</span>);
    const block = m.severity === 'BLOCK';
    parts.push(
      <mark
        key={`m${i}`}
        title={`${m.term} · ${block ? 'Chặn hẳn' : 'Cần xem'}`}
        style={{
          padding: '0 2px',
          borderRadius: 4,
          fontWeight: 600,
          color: block ? colors.dangerInk : colors.warningInk,
          background: block ? colors.dangerSoft : colors.warningSoft,
        }}
      >
        {text.slice(m.start, m.end)}
      </mark>
    );
    cursor = m.end;
  });
  if (cursor < text.length) parts.push(<span key="tail">{text.slice(cursor)}</span>);
  return <span style={{ whiteSpace: 'pre-wrap' }}>{parts}</span>;
};

// ------------------------------------------------------------------ sổ kiểm duyệt

/** Cột "Chi tiết" của một dòng sổ. */
export const LogSummary = ({ row }: { row: FeedLogRow }) => {
  const labels = toStringList(row.labels);
  const bits: React.ReactNode[] = [];
  if (row.note) bits.push(<span key="note">{row.note}</span>);
  // Backend lưu matched_words dạng chuỗi JSON ('["từ"]') → đọc dễ dãi thành danh sách (03 §12).
  const words = toStringList(row.matchedWords);
  if (words.length) bits.push(<span key="words">Từ khớp: {words.join(', ')}</span>);
  if (labels.length) {
    bits.push(<span key="labels">Nhãn: {labels.map((l) => LOG_LABEL_TEXT[l] || l).join(', ')}</span>);
  }
  if (row.errorMessage) {
    bits.push(
      <span key="err" style={{ color: colors.dangerInk }}>
        {row.errorMessage}
      </span>
    );
  }
  if (row.provider) {
    bits.push(
      <span key="prov" style={{ color: colors.textSecondary }}>
        {row.provider}
        {row.latencyMs !== null && row.latencyMs !== undefined ? `, ${row.latencyMs} ms` : ''}
      </span>
    );
  }
  if (!bits.length) return <span style={{ color: colors.textMuted }}>—</span>;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2, fontSize: 13, wordBreak: 'break-word' }}>
      {bits}
    </div>
  );
};

export const LogVerdict = ({ verdict, score }: { verdict?: string | null; score?: number | null }) => {
  if (!verdict && (score === null || score === undefined)) return <span style={{ color: colors.textMuted }}>—</span>;
  return (
    <span style={{ display: 'inline-flex', flexDirection: 'column', gap: 2, alignItems: 'flex-start' }}>
      <VerdictPill verdict={verdict} />
      {score !== null && score !== undefined && (
        <span style={{ fontSize: 12, color: colors.textSecondary }}>điểm {fmtScore(score)}</span>
      )}
    </span>
  );
};

const snapshotSide = (side: any) => {
  if (side === null || side === undefined) return <span style={{ color: colors.textMuted }}>(trống)</span>;
  if (typeof side !== 'object') return <span style={{ whiteSpace: 'pre-wrap' }}>{String(side)}</span>;
  const content = side.content ?? side.text;
  const media = side.mediaIds ?? side.mediaKeys;
  const rest = { ...side };
  delete rest.content;
  delete rest.text;
  delete rest.mediaIds;
  delete rest.mediaKeys;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {content !== undefined && (
        <div style={{ whiteSpace: 'pre-wrap' }}>{content === null || content === '' ? '(không có chữ)' : String(content)}</div>
      )}
      {Array.isArray(media) && media.length > 0 && (
        <div style={{ fontSize: 12, color: colors.textSecondary }}>Ảnh/video: {media.join(', ')}</div>
      )}
      {Object.keys(rest).length > 0 && <pre style={preStyle}>{JSON.stringify(rest, null, 2)}</pre>}
    </div>
  );
};

const preStyle: React.CSSProperties = {
  margin: 0,
  maxHeight: 240,
  overflow: 'auto',
  padding: 8,
  borderRadius: 8,
  background: colors.surfaceMuted,
  fontSize: 12,
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
};

/** Phần mở rộng của một dòng sổ: Trước/Sau, dữ liệu gốc máy soi (chỉ ADMIN có), IP (chỉ ADMIN). */
export const LogDetail = ({ row }: { row: FeedLogRow }) => {
  const snapshot: any = parseLoose(row.snapshot);
  const raw = parseLoose(row.raw);
  const hasBeforeAfter =
    snapshot && typeof snapshot === 'object' && ('before' in snapshot || 'after' in snapshot);
  const hasSnapshot = snapshot !== null && snapshot !== undefined && snapshot !== '';
  const hasRaw = raw !== null && raw !== undefined && raw !== '';
  if (!hasSnapshot && !hasRaw && !row.ip) {
    return <span style={{ color: colors.textMuted }}>Không có dữ liệu thêm.</span>;
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {hasBeforeAfter ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 12 }}>
          <div>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>Trước</div>
            {snapshotSide(snapshot.before)}
          </div>
          <div>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>Sau</div>
            {snapshotSide(snapshot.after)}
          </div>
        </div>
      ) : hasSnapshot ? (
        <div>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>Dữ liệu lúc ghi sổ</div>
          {snapshotSide(snapshot)}
        </div>
      ) : null}
      {hasRaw && (
        <details>
          <summary style={{ cursor: 'pointer', fontWeight: 600 }}>Dữ liệu gốc từ máy soi</summary>
          <pre style={{ ...preStyle, marginTop: 6 }}>
            {typeof raw === 'string' ? raw : JSON.stringify(raw, null, 2)}
          </pre>
        </details>
      )}
      {row.ip && <div style={{ fontSize: 12, color: colors.textSecondary }}>IP: {row.ip}</div>}
    </div>
  );
};

/** Tối đa `max` nhãn lý do, còn lại "+N" có tooltip đủ danh sách. */
export const ReasonTags = ({
  reasons,
  labelOf,
  max = 2,
}: {
  reasons: string[];
  labelOf: (value: string) => string;
  max?: number;
}) => {
  if (!reasons?.length) return <span style={{ color: colors.textMuted }}>—</span>;
  const shown = reasons.slice(0, max);
  const rest = reasons.slice(max);
  return (
    <span style={{ display: 'inline-flex', flexWrap: 'wrap', gap: 4 }}>
      {shown.map((r) => (
        <Pill key={r} color={colors.textPrimary} bg={colors.surfaceMuted}>
          {labelOf(r)}
        </Pill>
      ))}
      {rest.length > 0 && (
        <Tooltip title={reasons.map(labelOf).join(' · ')}>
          <span>
            <Pill color={colors.textSecondary} bg={colors.surfaceMuted}>
              +{rest.length}
            </Pill>
          </span>
        </Tooltip>
      )}
    </span>
  );
};

/** Khung "Vì sao cần xem" / "Máy kiểm tra": từ khớp + kết quả AI. */
export const WhyFlagged = ({
  keywordHits,
  textVerdict,
  mediaVerdicts,
}: {
  keywordHits?: string[] | null;
  textVerdict?: string | null;
  mediaVerdicts?: string[] | null;
}) => {
  const hits = keywordHits || [];
  const media = (mediaVerdicts || []).filter((v) => v && v !== 'OK' && v !== 'SKIPPED');
  const worstMedia = media.includes('BLOCK') ? 'BLOCK' : media.includes('REVIEW') ? 'REVIEW' : media[0];
  const showText = textVerdict && textVerdict !== 'SKIPPED';
  if (!hits.length && !showText && !worstMedia) return null;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
      {hits.map((w) => (
        <Pill key={w} color={colors.warningInk} bg={colors.warningSoft}>
          {w}
        </Pill>
      ))}
      {showText && <VerdictPill verdict={textVerdict} prefix="AI chữ" />}
      {worstMedia && <VerdictPill verdict={worstMedia} prefix="AI ảnh" />}
    </div>
  );
};
